import type { TbHttp } from "./http";
import { getAttributes } from "./telemetry";
import { listRelationsFrom } from "./relations";
import { getEntityInfo, getCurrentUser } from "./entities";
import type { EntityRef, PageData, TbEntity } from "./types";
import type { BessConfig, SiteTopology, SmartmeterConfig } from "../types";

/**
 * Thrown when discovery finds no usable BESS-site under the bound entity alias
 * (no `EMS Installation` asset visible, or the bound entity resolves to a site
 * with zero BESS devices). Distinct from auth/network failures so the store can
 * react differently: a *no-assets* result drops into the mock SIMULATOR (so a
 * ThingsBoard demo page works with any/no entity bound), whereas an auth/network
 * failure surfaces the "live data unavailable" error.
 */
export class NoSiteAssetsError extends Error {
  constructor(message = "No BESS-site assets found under the bound entity.") {
    super(message);
    this.name = "NoSiteAssetsError";
  }
}

/**
 * Discover a {@link SiteTopology} from the TB Contains hierarchy rooted at
 * an `EMS Installation` site asset. Updated against the real CPC Inox
 * tenant shape — see [`TB_FINDINGS.md`](../../../TB_FINDINGS.md) for the
 * full investigation.
 *
 * Real-world shape:
 *
 *   "EMS Customer Grouping Asset"
 *     └─ Contains
 *         └─ "EMS Installation" (one per physical site)
 *             ├─ Contains            BESS-DEVICES-*    (1..N)
 *             ├─ Contains            BESS-GRID-DEVICES (1)
 *             ├─ Contains|ContainsController   BESS-CONTROLLER-DEVICES (1)
 *             └─ Contains            BESS-AUX-DEVICES  (0..N — transformers on Basiano-style sites)
 *
 *   The `ContainsController` relation type is deprecated; the SPA accepts
 *   either `Contains` or `ContainsController` when looking for the
 *   controller so the migration can happen on TB's side without breaking
 *   us.
 */
export interface TbSiteContext {
  site: EntityRef;
  siteName: string;
  topology: SiteTopology;
  refs: {
    grid: EntityRef | null;
    controller: EntityRef | null;
    bess: Record<string, EntityRef>;
    pv: EntityRef | null;
    /** Auxiliary devices (transformers etc.). Carry frequency/voltage/PF
     *  that the Grid device doesn't always have. */
    aux: EntityRef[];
  };
  /** AE2 daily-totals attribute keys per BESS device (resolved during build). */
  bessConsumptionAttrs: Record<string, EntityRef>;
}

// Profile names seen on the live tenant
const SITE_PROFILES = new Set(["EMS Installation", "site"]);
const GROUPING_PROFILES = new Set(["EMS Customer Grouping Asset"]);
const GRID_PROFILES = new Set(["BESS-GRID-DEVICES", "grid"]);
const CONTROLLER_PROFILES = new Set(["BESS-CONTROLLER-DEVICES", "controller"]);
const BESS_PROFILE_PREFIX = "BESS-DEVICES";
const AUX_PROFILES = new Set(["BESS-AUX-DEVICES"]);
const PV_PROFILES = new Set(["pv", "BESS-PV-DEVICES"]);

// Controller-edge relation types we tolerate (Gessate still uses the
// deprecated ContainsController; Basiano uses plain Contains).
const CONTROLLER_EDGES = new Set(["Contains", "ContainsController"]);

/** Discover every site (`EMS Installation`) the user can see. */
export async function discoverSites(http: TbHttp): Promise<TbEntity[]> {
  const me = await getCurrentUser(http);
  const list = await listAccessibleAssets(http, me);
  return list.filter((a) => SITE_PROFILES.has(a.type));
}

/** Find the top-level grouping asset (optional — may not exist for some tenants). */
export async function discoverGrouping(http: TbHttp): Promise<TbEntity | null> {
  const me = await getCurrentUser(http);
  const list = await listAccessibleAssets(http, me);
  return list.find((a) => GROUPING_PROFILES.has(a.type)) ?? null;
}

async function listAccessibleAssets(http: TbHttp, me: { authority: string; customerId?: { id: string } }): Promise<TbEntity[]> {
  const out: TbEntity[] = [];
  const isCustomer = me.authority === "CUSTOMER_USER";
  const base = isCustomer && me.customerId
    ? `/api/customer/${me.customerId.id}/assets`
    : `/api/tenant/assets`;
  let page = 0;
  while (true) {
    const p = new URLSearchParams({
      pageSize: "100",
      page: String(page),
      sortProperty: "createdTime",
      sortOrder: "DESC",
    });
    const r = await http.get<PageData<TbEntity>>(`${base}?${p}`);
    out.push(...r.data);
    if (!r.hasNext) break;
    page++;
  }
  return out;
}

/** Build the full SiteTopology for a given site asset. */
export async function buildSiteContext(http: TbHttp, site: TbEntity): Promise<TbSiteContext> {
  const root: EntityRef = { type: "ASSET", id: site.id.id };

  // Site attributes — operator-facing metadata + counters
  const siteAttrs = await getAttributes(http, root, "SERVER_SCOPE", [
    "address", "lon", "lat", "picture",
    "NrOfBess", "InstalledTotalPower", "InstalledTotalCapacity",
    "alarmState", "alarmsCount", "unackedAlarmsCount", "highestAlarmSeverity",
    "tz_string",
  ]).catch(() => []);
  const tz = String(getAttr(siteAttrs, "tz_string") ?? "Europe/Rome");
  const installedCapacity = Number(getAttr(siteAttrs, "InstalledTotalCapacity") ?? 0);

  // Walk all outgoing relations once — keep every type so we can classify
  // by `Contains` / `ContainsController` etc. Profile name is read from
  // the entity itself (lazy).
  const rels = await listRelationsFrom(http, root);

  let grid: EntityRef | null = null;
  let controller: EntityRef | null = null;
  let pv: EntityRef | null = null;
  const aux: EntityRef[] = [];
  const bessRefs: { ref: EntityRef; name: string }[] = [];

  for (const r of rels) {
    const child: EntityRef = { type: r.to.entityType as EntityRef["type"], id: r.to.id };
    if (child.type !== "DEVICE") continue;
    const info = await getEntityInfo(http, child).catch(() => null);
    if (!info) continue;
    const profile = info.type;
    const name = info.name;

    if (GRID_PROFILES.has(profile)) {
      grid = child;
    } else if (CONTROLLER_PROFILES.has(profile)) {
      // Accept whether the edge is Contains or ContainsController.
      if (CONTROLLER_EDGES.has(r.type)) controller = child;
    } else if (profile.startsWith(BESS_PROFILE_PREFIX)) {
      bessRefs.push({ ref: child, name });
    } else if (AUX_PROFILES.has(profile)) {
      aux.push(child);
    } else if (PV_PROFILES.has(profile)) {
      pv = child;
    }
  }

  // Sort BESS by name so the SPA's per-unit list is stable across reloads.
  bessRefs.sort((a, b) => a.name.localeCompare(b.name));

  // Read per-BESS metadata
  const bessConfigs: BessConfig[] = await Promise.all(
    bessRefs.map((b, i) => buildBessConfig(http, b, i))
  );

  // Synth a smartmeter row when grid is present (preserves SPA's
  // SmartmeterConfig contract so existing pages keep working).
  const smartmeters: SmartmeterConfig[] = [];
  if (grid) {
    smartmeters.push({
      id: "sm-grid",
      name: "Grid coupling",
      role: "grid",
      protocol: "modbus-tcp",
      host: "—", port: 0,
      factor: 1,
      topics: { power_W: "smartmeter/aggregation/grid_W" },
    });
  }
  if (pv) {
    smartmeters.push({
      id: "sm-pv",
      name: "PV inverter feed",
      role: "pv",
      protocol: "modbus-tcp",
      host: "—", port: 0,
      factor: 1,
      topics: { power_W: "pv/measurement/1/power_W" },
    });
  }

  const topology: SiteTopology = {
    id: site.id.id,
    name: site.label || site.name,
    shortLabel: (site.label || site.name).split(" ")[0] || site.name.slice(0, 6),
    timezone: tz,
    aggregation: {
      grid_W: "smartmeter/aggregation/grid_W",
      battery_power_W: "battery/aggregation/power",
      battery_soc_avg: "battery/aggregation/SOC",
      pv_power_W: pv ? "pv/measurement/1/power_W" : undefined,
    },
    bess: bessConfigs,
    smartmeters,
    controller: {
      id: controller?.id ?? "controller-1",
      grid_smartmeter_id: "sm-grid",
      modes: ["gridZeroControl", "PeakShaving", "ConstantPower", "OFF"],
      schedule_attribute: "bess_schedule",
      diagnostic_topic: "controller/diagnostics",
    },
    prices: {
      zone: "IT-NORD",
      currency: "EUR",
      day_ahead_attribute: "next_day_prices",
      carbon_intensity_g_kWh: 245,
    },
    scadaPreset: bessConfigs.length === 1 ? "compact" : "wide",
  };

  // Sanity: if InstalledTotalCapacity exists but per-BESS capacities are 0
  // (because device attrs weren't read), distribute the total evenly.
  if (
    installedCapacity > 0
    && bessConfigs.every((b) => b.capacity_kWh === 0)
  ) {
    const each = installedCapacity / Math.max(1, bessConfigs.length);
    for (const b of bessConfigs) b.capacity_kWh = each;
  }

  const refsByBessId: Record<string, EntityRef> = {};
  bessRefs.forEach((b, i) => { refsByBessId[bessConfigs[i].id] = b.ref; });

  return {
    site: root,
    siteName: site.label || site.name,
    topology,
    refs: { grid, controller, bess: refsByBessId, pv, aux },
    bessConsumptionAttrs: refsByBessId,
  };
}

function getAttr(attrs: Array<{ key: string; value: unknown }>, key: string): unknown {
  return attrs.find((a) => a.key === key)?.value;
}

async function buildBessConfig(
  http: TbHttp,
  b: { ref: EntityRef; name: string },
  idx: number,
): Promise<BessConfig> {
  const attrs = await getAttributes(http, b.ref, "SERVER_SCOPE", [
    "InstalledCapacity", "InstalledPower", "bessType",
    "host", "port", "protocol",
    "invert_active_power_measurement", "invert_active_power_setpoint",
    "invert_reactive_power_measurement", "invert_reactive_power_setpoint",
  ]).catch(() => []);

  const bessType = String(getAttr(attrs, "bessType") ?? "");
  const family: BessConfig["family"] =
    /zhiguang/i.test(bessType) ? "zhiguang"
    : /monet/i.test(bessType) ? "monet"
    : "generic";
  // Telemetry-cycle id used by the SPA's per-unit array — stable per
  // session. The real TB entity id lives in `site.refs.bess[<id>]`.
  const id = `bess-${idx + 1}`;
  return {
    id,
    name: b.name,
    family,
    protocol: (String(getAttr(attrs, "protocol") ?? "iec-104")) as BessConfig["protocol"],
    host: String(getAttr(attrs, "host") ?? "—"),
    port: Number(getAttr(attrs, "port") ?? 0),
    capacity_kWh: Number(getAttr(attrs, "InstalledCapacity") ?? 0),
    max_charge_kW: Number(getAttr(attrs, "InstalledPower") ?? 0),
    max_discharge_kW: Number(getAttr(attrs, "InstalledPower") ?? 0),
    invert: {
      active_power_measurement: Boolean(getAttr(attrs, "invert_active_power_measurement")),
      active_power_setpoint:    Boolean(getAttr(attrs, "invert_active_power_setpoint")),
      reactive_power_measurement: Boolean(getAttr(attrs, "invert_reactive_power_measurement")),
      reactive_power_setpoint:    Boolean(getAttr(attrs, "invert_reactive_power_setpoint")),
    },
    topics: {
      soc: `battery/measurement/${idx + 1}/SOC`,
      ac_active_power: `battery/measurement/${idx + 1}/ac_active_power`,
      ac_reactive_power: `battery/measurement/${idx + 1}/ac_reactive_power`,
      total_voltage: `battery/measurement/${idx + 1}/Total_Voltage`,
      ambient_temperature: `battery/measurement/${idx + 1}/ambient_temperature`,
      accumulated_charge: `battery/measurement/${idx + 1}/accumulated/positive`,
      accumulated_discharge: `battery/measurement/${idx + 1}/accumulated/negative`,
      fault_flags_prefix: `battery/measurement/${idx + 1}/fault_`,
      setpoint_active: `controller/${idx + 1}/output/power_W`,
      setpoint_reactive: `controller/${idx + 1}/output/power_VAr`,
      bess_onoff: `controller/${idx + 1}/output/BESS_ONOFF`,
      dc_relays: `controller/${idx + 1}/output/DCRelays`,
    },
  };
}
